<template>
  <v-table class="mb-2">
    <thead>
      <tr>
        <th
          v-for="column in FIRMWARE_BULK_UPDATE_SETTINGS_TABLE_HEADERS"
          :key="column"
        >
          {{ column }}
        </th>
      </tr>
    </thead>
    <tbody>
      <tr
        v-for="item in bulkUpdateList"
        :key="item.id"
      >
        <td>{{ item.id }}</td>
        <td>{{ item.activation_date }}</td>
        <td>{{ item.firmware?.version }}</td>
        <td>{{ item.target_device_count }}</td>
        <td>{{ item.exclusion_device_count }}</td>
        <td>{{ formatDateTime(new Date(item.updated_at)) }}</td>
        <td>{{ item.account?.full_name }}</td>
        <td>
           <EditLinkIcon :name="'FirmwareBulkUpdateForm'" :id="item.id" />
        </td>
        <td>
          <DeleteIcon
            v-if="canDelete(item.activation_date)"
            :deleteAction="() => deleteBulkUpdateSetting(item.id)"
            :isDeleteAuthority="isDeleteAuthority"
            :tooltipMessage="i18n.global.t('pages.firmwareBulkUpdate.tooltip.authority')"
          />
        </td>
      </tr>
    </tbody>
  </v-table>
  <PaginateWithCount
    class="justify-end"
    :totalPage="totalPage"
    v-model="currentPage"
    :itemsPerPage="itemsPerPage"
    :allElementsCount="allElementsCount"
    :showItemCount="true"
  />
</template>

<script setup lang="ts">
import { ref, onMounted, watch } from 'vue';
import Api from '@/services/api/ApiServiceFabrick';
import AuthorityCacheApi from "@/services/models/AuthorityCacheApi"
import { useAuth } from "@/services/user/Auth0UserProvider"
import { formatDateTime } from '@/services/functions'
import EditLinkIcon from '@/components/Common/EditLinkIcon.vue'
import DeleteIcon from '@/components/Common/DeleteIcon.vue'
import PaginateWithCount from '@/components/PaginateWithCount.vue'
import getFirmwareBulkUpdateSettingsRequest from '@/types/requests/getFirmwareBulkUpdateSettingsRequest';
import getFirmwareBulkUpdateSettingResponse from '@/types/responses/getFirmwareBulkUpdateSettingResponse';
import { FIRMWARE_BULK_UPDATE_SETTINGS_TABLE_HEADERS } from '@/services/consts/firmware_bulk_update';
import i18n from '@/lang/i18n';

const { loginWithRedirect } = useAuth();
const bulkUpdateList = ref<getFirmwareBulkUpdateSettingResponse[]>()
const currentPage = ref<number>(1)
const totalPage = ref<number>(1)
const itemsPerPage = ref<number>(15)
const allElementsCount = ref<number>(0)
const isDeleteAuthority = ref<boolean>(false)

const props = defineProps<{
  showFlash: (message: string, type: string, fadeout: boolean) => void
}>()

onMounted(() => {
  getDeleteAuthority().then((authority) => {
    isDeleteAuthority.value = authority
  })
  getBulkUpdateList()
})

function makeParams() {
  const params = {
    page: currentPage.value
  } as getFirmwareBulkUpdateSettingsRequest

  return params
}

function getBulkUpdateList() {
  const params = makeParams()
  Api().getFirmwareBulkUpdateSettings(params).then((data) => {
    bulkUpdateList.value = data.data
    currentPage.value = data.current_page
    totalPage.value = data.last_page
    allElementsCount.value = data.total
    itemsPerPage.value = data.per_page
    }).catch((error) => {
      const res = error.response
      switch (res.status) {
        case 401:
          loginWithRedirect({ appState: { targetUrl: location.pathname } })
          break
        case 403:
          location.reload()
          break
        default:
          props.showFlash(res.data.message, 'error', true)
          break
      }
    }
  )
}

watch(currentPage, () => {
  getBulkUpdateList()
})

function canDelete(activationDate: string) {
  const now = new Date()
  const activation = new Date(activationDate)
  return activation > now;
}

async function getDeleteAuthority() {
  const authorityList = await AuthorityCacheApi.getWithParse();
  return !!authorityList["octlink.firmware.update"];
}

async function deleteBulkUpdateSetting(id: number) {
  await Api().deleteFirmwareBulkUpdateSetting(id).then(() => {
    props.showFlash(i18n.global.t('pages.common.messages.successDelete'), 'success', true)
    getBulkUpdateList()
  }).catch((error) => {
    const res = error.response
    switch (res.status) {
      case 400:
        props.showFlash(i18n.global.t('pages.accounts.messages.badRequestForDelete'), 'error', true)
        break
      case 403:
        props.showFlash(i18n.global.t('pages.common.messages.forbidden'), 'error', true)
        break
      default:
        props.showFlash(i18n.global.t('pages.common.messages.failedDelete'), 'error', true)
        break
    }
  })
}

</script>
